import React, {useState, useEffect, useCallback} from 'react';
import {
    ChartBarIcon,
    UserGroupIcon,
    FunnelIcon,
    ArrowDownTrayIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

const categoryColors = {
    Purchase: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    SessionBooking: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    SessionCancellation: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    SubscriptionAllocation: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    ManualAdjustment: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    Refund: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const TokenHistoryReport = () => {
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tokenHistory, setTokenHistory] = useState([]);
    const [summary, setSummary] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    
    // Filter states
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedUser, setSelectedUser] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day'));
    const [toDate, setToDate] = useState(dayjs());
    const [isSiteAdmin, setIsSiteAdmin] = useState(false);

    // Get unique values for filters
    const categories = [...new Set(tokenHistory.map(t => t.categoryName))];
    const users = Array.from(
        new Map(tokenHistory.map(t => [t.userId, { id: t.userId, name: t.userName, email: t.userEmail }])).values()
    );

    const handleCreateVenueAudit = async () => {
        if (!currentVenueKey) {
            alert('Please select a venue first');
            return;
        }

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/TokenHistory/admin/audit/venue/${currentVenueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                alert('Venue audit created successfully!');
                // Refresh the token history to show the new audit transaction
                await fetchTokenHistory();
            } else {
                const errorText = await response.text();
                alert(`Failed to create venue audit: ${errorText}`);
            }
        } catch (error) {
            console.error('Error creating venue audit:', error);
            alert('An error occurred while creating the venue audit');
        }
    };

    const fetchTokenHistory = useCallback( async () => {
        setLoading(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const params = new URLSearchParams();
            if (fromDate) params.append('from', fromDate.toISOString());
            if (toDate) params.append('to', toDate.toISOString());

            const response = await fetch(`${baseUrl}/api/TokenHistory/venue/${currentVenueKey}?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setTokenHistory(data);
            }
        } catch (error) {
            console.error('Error fetching token history:', error);
        } finally {
            setLoading(false);
        }
    }, [currentVenueKey, fromDate, toDate]);

    const fetchSummary = useCallback(async () => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const params = new URLSearchParams();
            if (fromDate) params.append('from', fromDate.toISOString());
            if (toDate) params.append('to', toDate.toISOString());

            const response = await fetch(`${baseUrl}/api/TokenHistory/venue/${currentVenueKey}/summary?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setSummary(data);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    }, [currentVenueKey, fromDate, toDate]);

    const applyFilters = useCallback( () => {
        let filtered = [...tokenHistory];

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(t => t.categoryName === selectedCategory);
        }

        // Filter by user
        if (selectedUser !== 'all') {
            filtered = filtered.filter(t => t.userId === parseInt(selectedUser));
        }

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t =>
                t.userName.toLowerCase().includes(term) ||
                t.userEmail.toLowerCase().includes(term) ||
                t.notes?.toLowerCase().includes(term)
            );
        }

        setFilteredData(filtered);
    }, [tokenHistory, selectedCategory, selectedUser, searchTerm]);
    
    useEffect(() => {
        Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
        const checkSiteAdmin = async () => {
            const siteAdmin = await Helper.userIsSiteAdmin();
            setIsSiteAdmin(siteAdmin);
        };

        checkSiteAdmin();
    }, []);

    useEffect(() => {
        if (currentVenueKey) {
            fetchTokenHistory();
            fetchSummary();
        }
    }, [currentVenueKey, fetchTokenHistory, fetchSummary]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

   
    if(!venueKeyOptions){
        console.warn("No venue options available");
        return null;
    }
    const exportToCSV = () => {
        const headers = ['Date', 'Time', 'User', 'Email', 'Category', 'Tokens', 'Notes', 'Reference'];
        const rows = filteredData.map(t => [
            new Date(t.createdOn).toLocaleDateString(),
            new Date(t.createdOn).toLocaleTimeString(),
            t.userName,
            t.userEmail,
            t.categoryName,
            t.tokenChange,
            t.notes || '',
            t.referenceId || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `token-history-${currentVenueKey}-${dayjs().format('YYYY-MM-DD')}.csv`;
        a.click();
    };

    const formatTokenChange = (change) => {
        if (change > 0) {
            return <span className="text-green-400 font-semibold">+{change}</span>;
        } else if (change < 0) {
            return <span className="text-red-400 font-semibold">{change}</span>;
        }
        return <span className="text-gray-400">{change}</span>;
    };

    const getCategoryBadge = (categoryName) => {
        const colorClass = categoryColors[categoryName] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        return (
            <span className={`px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
                {categoryName}
            </span>
        );
    };

    if (loading) {
        return (
            <LayoutWrapper title="Token History Report">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="Token History Report"
            subtitle="Comprehensive token transaction reporting for administrators"
        >
            <div className="space-y-6">
                {/* Summary Cards */}
                {summary && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="card p-6 bg-gradient-to-br from-emerald-900/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Total Purchased</p>
                                    <p className="text-3xl font-bold text-emerald-400 mt-1">
                                        {summary.totalTokensPurchased}
                                    </p>
                                </div>
                                <ChartBarIcon className="h-12 w-12 text-emerald-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6 bg-gradient-to-br from-sky-900/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Spent on Sessions</p>
                                    <p className="text-3xl font-bold text-sky-400 mt-1">
                                        {summary.totalTokensUsed}
                                    </p>
                                </div>
                                <ChartBarIcon className="h-12 w-12 text-sky-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6 bg-gradient-to-br from-amber-900/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Cancelled Sessions</p>
                                    <p className="text-3xl font-bold text-amber-400 mt-1">
                                        {summary.totalTokensReturned}
                                    </p>
                                </div>
                                <ChartBarIcon className="h-12 w-12 text-amber-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6 bg-gradient-to-br from-purple-900/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Unique Users</p>
                                    <p className="text-3xl font-bold text-purple-400 mt-1">
                                        {summary.uniqueUsers}
                                    </p>
                                </div>
                                <UserGroupIcon className="h-12 w-12 text-purple-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6 bg-gradient-to-br from-violet-900/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Subscription Tokens</p>
                                    <p className="text-3xl font-bold text-violet-400 mt-1">
                                        {summary.totalSubscriptionAllocations}
                                    </p>
                                </div>
                                <ChartBarIcon className="h-12 w-12 text-violet-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6 bg-gradient-to-br from-rose-900/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Manual Adjustments</p>
                                    <p className="text-3xl font-bold text-rose-400 mt-1">
                                        {summary.totalManualAdjustments}
                                    </p>
                                </div>
                                <ChartBarIcon className="h-12 w-12 text-rose-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6 bg-gradient-to-br from-slate-700/30 to-gray-900">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">Total Transactions</p>
                                    <p className="text-3xl font-bold text-slate-100 mt-1">
                                        {summary.transactionCount}
                                    </p>
                                </div>
                                <ChartBarIcon className="h-12 w-12 text-slate-400 opacity-50" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="card p-6">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                        <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                            <FunnelIcon className="h-5 w-5" />
                            Filters & Search
                        </h3>
                        <button
                            onClick={exportToCSV}
                            className="btn-primary flex items-center gap-2"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                            Export CSV
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Search
                            </label>
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search users, notes..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Category
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                User
                            </label>
                            <select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Users</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.name} ({user.email})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                From Date
                            </label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={fromDate}
                                    onChange={setFromDate}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined",
                                            size: "small"
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                To Date
                            </label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={toDate}
                                    onChange={setToDate}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined",
                                            size: "small"
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                <div className="card p-4 bg-blue-900/20 border border-blue-500/30">
                    <p className="text-blue-300 text-sm">
                        Showing <span className="font-bold">{filteredData.length}</span> of <span className="font-bold">{tokenHistory.length}</span> transactions
                    </p>
                </div>

                {/* Transaction Table */}
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Date & Time
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Tokens
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Reference
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                                            No transactions found for the selected filters
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((transaction) => (
                                        <tr key={transaction.id} className="hover:bg-gray-800/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm">
                                                    <div className="text-white font-medium">
                                                        {new Date(transaction.createdOn).toLocaleDateString()}
                                                    </div>
                                                    <div className="text-gray-500 text-xs">
                                                        {new Date(transaction.createdOn).toLocaleTimeString()}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="text-white font-medium">{transaction.userName}</div>
                                                    <div className="text-gray-500 text-xs">{transaction.userEmail}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getCategoryBadge(transaction.categoryName)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-white max-w-md">
                                                    {transaction.notes || transaction.categoryName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {formatTokenChange(transaction.tokenChange)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-400">
                                                {transaction.referenceId ? `#${transaction.referenceId}` : '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Legend */}
                <div className="card p-6">
                    <h3 className="text-white font-semibold mb-4">Transaction Type Legend</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                            <span className="text-sm text-gray-300">Token Purchase</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-sky-500"></div>
                            <span className="text-sm text-gray-300">Session Booking</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                            <span className="text-sm text-gray-300">Session Cancelled</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                            <span className="text-sm text-gray-300">Subscription Allocation</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                            <span className="text-sm text-gray-300">Manual Adjustment</span>
                        </div>
                    </div>
                </div>

                {/* Site Admin - Venue Audit */}
                {isSiteAdmin && (
                    <div className="card p-6">
                        <h3 className="text-white font-semibold mb-4">Site Admin Actions</h3>
                        <div className="flex flex-col gap-3">
                            <p className="text-sm text-gray-300">
                                Create a venue audit to verify token balances and transaction integrity for this venue.
                            </p>
                            <button
                                onClick={handleCreateVenueAudit}
                                disabled={!currentVenueKey}
                                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2 w-full md:w-auto"
                            >
                                <ChartBarIcon className="h-5 w-5" />
                                Create Venue Audit
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </LayoutWrapper>
    );
};

export default TokenHistoryReport;

